import React, { useState } from 'react';
import SEO from '../components/SEO';
import seoData from '../config/seoData';
import { Helmet } from 'react-helmet-async';
import { generateFAQPage } from '../utils/structuredData';
import { Phone, MessageCircle, MapPin, CheckCircle, Wrench, Building2, Warehouse, Factory, Store, Home, Hospital, School, Shield, Clock, Award, Instagram, Menu, X, ChevronRight, Sparkles, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useNavigate, Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';

const LimpezaPosObra = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = (contexto = 'geral') => {
    const mensagens = {
      geral: 'Olá! Gostaria de solicitar um orçamento de limpeza pós-obra.',
      galpao: 'Olá! Preciso de limpeza pós-obra para um galpão. Gostaria de receber um orçamento.',
      industrial: 'Olá! Tenho uma área industrial e preciso de limpeza pós-obra profissional. Gostaria de um orçamento.',
      orcamento: 'Olá! Gostaria de solicitar uma avaliação e orçamento de limpeza pós-obra.',
      locacao: 'Olá! Gostaria de informações sobre locação de equipamentos para limpeza pós-obra.',
      info: 'Olá! Gostaria de saber mais sobre os serviços de limpeza pós-obra da WellTec.'
    };
    
    const message = encodeURIComponent(mensagens[contexto] || mensagens.geral);
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  const servicosLimpeza = [
    {
      title: 'Limpeza de Pisos',
      description: 'Remoção de poeira, sujeira pesada, resíduos de obra e manchas superficiais utilizando equipamentos profissionais.',
      icon: <Sparkles className="w-8 h-8" />
    },
    {
      title: 'Limpeza de Galpões',
      description: 'Limpeza de grandes áreas industriais, logísticas e comerciais após obras ou reformas.',
      icon: <Warehouse className="w-8 h-8" />
    },
    {
      title: 'Limpeza de Garagens',
      description: 'Remoção de poeira, resíduos, lama e sujeira pesada em estacionamentos e garagens.',
      icon: <Truck className="w-8 h-8" />
    },
    {
      title: 'Limpeza Industrial',
      description: 'Limpeza de áreas industriais após reformas, adequações ou finalização de obras.',
      icon: <Factory className="w-8 h-8" />
    },
    {
      title: 'Limpeza de Condomínios',
      description: 'Limpeza de áreas comuns, halls, garagens, corredores e áreas externas após obras.',
      icon: <Building2 className="w-8 h-8" />
    },
    {
      title: 'Limpeza Comercial',
      description: 'Preparação de lojas, supermercados, clínicas, escritórios e estabelecimentos antes da abertura.',
      icon: <Store className="w-8 h-8" />
    },
    {
      title: 'Limpeza Pós-Reforma',
      description: 'Limpeza pesada após reformas e intervenções em imóveis comerciais ou residenciais.',
      icon: <Home className="w-8 h-8" />
    }
  ];

  const equipamentos = [
    { name: 'Lavadoras de Piso', benefit: 'Limpeza profunda e eficiente' },
    { name: 'Varredeiras Industriais', benefit: 'Produtividade em grandes áreas' },
    { name: 'Aspiradores Profissionais', benefit: 'Remoção de poeira fina' },
    { name: 'Equipamentos de Alta Pressão', benefit: 'Limpeza pesada e manchas' },
    { name: 'Extratoras', benefit: 'Limpeza profunda de superfícies' },
    { name: 'Equipamentos Especializados', benefit: 'Solução para cada tipo de piso' }
  ];

  const clientesAtendidos = [
    { 
      name: 'Construtoras e Incorporadoras', 
      desc: 'Limpeza final para preparação da obra antes da entrega ao cliente',
      icon: <Building2 className="w-6 h-6" /> 
    },
    { 
      name: 'Galpões Logísticos e Centros de Distribuição', 
      desc: 'Limpeza mecanizada de grandes áreas após obras e adequações',
      icon: <Warehouse className="w-6 h-6" /> 
    },
    { 
      name: 'Indústrias', 
      desc: 'Limpeza de pisos e áreas industriais após reformas e intervenções',
      icon: <Factory className="w-6 h-6" /> 
    },
    { 
      name: 'Condomínios', 
      desc: 'Limpeza de garagens, áreas comuns e espaços após obras',
      icon: <Building2 className="w-6 h-6" /> 
    },
    { 
      name: 'Lojas e Supermercados', 
      desc: 'Preparação do espaço antes da inauguração ou reabertura',
      icon: <Store className="w-6 h-6" /> 
    },
    { 
      name: 'Hospitais e Clínicas', 
      desc: 'Limpeza profissional pós-reforma seguindo padrões de higiene',
      icon: <Hospital className="w-6 h-6" /> 
    }
  ];

  const etapasProcesso = [
    {
      number: '01',
      title: 'Avaliação da área',
      description: 'Visitamos o local para entender o escopo e necessidades específicas'
    },
    {
      number: '02',
      title: 'Identificação do tipo de piso e nível de sujeira',
      description: 'Analisamos o material e grau de resíduos para definir a melhor abordagem'
    },
    {
      number: '03',
      title: 'Definição dos equipamentos e método de limpeza',
      description: 'Selecionamos os equipamentos profissionais adequados para cada situação'
    },
    {
      number: '04',
      title: 'Execução da limpeza',
      description: 'Nossa equipe realiza o serviço com equipamentos profissionais e técnicas adequadas'
    },
    {
      number: '05',
      title: 'Conferência e entrega da área limpa',
      description: 'Verificamos o resultado e entregamos o espaço pronto para uso'
    }
  ];

  const faqItems = [
    {
      question: 'Quanto custa uma limpeza pós-obra?',
      answer: 'O valor depende da metragem da área, tipo de piso, nível de sujeira, quantidade de resíduos, presença de cimento/argamassa/rejunte, acesso ao local e necessidade de equipamentos específicos. Entre em contato informando a metragem aproximada e a cidade para recebermos uma estimativa personalizada.'
    },
    {
      question: 'Quanto tempo demora uma limpeza pós-obra?',
      answer: 'O prazo varia conforme o tamanho da área e as condições do local. Em áreas menores, o serviço pode ser concluído em algumas horas. Em galpões e grandes áreas, pode levar de 1 a 3 dias. Após avaliação, informamos o prazo estimado.'
    },
    {
      question: 'Vocês atendem empresas e residências?',
      answer: 'Sim. Nosso foco principal são empresas, construtoras, galpões, indústrias e grandes áreas, mas também atendemos residências que necessitem de limpeza profissional pós-obra ou pós-reforma.'
    },
    {
      question: 'Vocês fazem limpeza pós-obra de galpões?',
      answer: 'Sim. Trabalhamos especialmente com galpões industriais, logísticos e centros de distribuição. Utilizamos equipamentos profissionais adequados para grandes áreas, o que aumenta a produtividade e melhora o resultado.'
    },
    {
      question: 'Vocês fazem limpeza industrial pós-obra?',
      answer: 'Sim. Realizamos limpeza de pisos e áreas industriais após obras, reformas e adequações, utilizando equipamentos profissionais selecionados conforme o tipo de piso e resíduo.'
    },
    {
      question: 'Vocês removem resíduos de cimento e argamassa?',
      answer: 'Sim. Utilizamos equipamentos e produtos adequados para remoção de resíduos de cimento, argamassa e rejunte, respeitando as características do piso. O método de limpeza é definido após avaliação do tipo de superfície.'
    },
    {
      question: 'Qual equipamento é utilizado na limpeza pós-obra?',
      answer: 'Utilizamos lavadoras de piso, varredeiras industriais, aspiradores profissionais, extratoras e outros equipamentos especializados. A seleção depende do tipo de piso, nível de sujeira, tamanho da área e objetivo da limpeza.'
    },
    {
      question: 'Vocês alugam os equipamentos sem operador?',
      answer: 'Sim. Você pode alugar os equipamentos para executar a limpeza com sua própria equipe ou contratar nossa equipe para realizar o serviço completo. Visite nossa página de locação de equipamentos para mais informações.'
    },
    {
      question: 'Quais cidades vocês atendem?',
      answer: 'Atendemos Belo Horizonte, Contagem, Betim, Nova Lima, Mário Campos e outras cidades da Região Metropolitana de Belo Horizonte.'
    },
    {
      question: 'Vocês fazem orçamento antes do serviço?',
      answer: 'Sim. Recomendamos uma avaliação prévia do local para fornecer um orçamento preciso. Em alguns casos, é possível fornecer uma estimativa inicial apenas com informações básicas como metragem, tipo de piso e cidade.'
    }
  ];

  // Structured Data - FAQPage
  const faqPageData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Component */}
      <SEO 
        title={seoData['/limpeza-pos-obra'].title}
        description={seoData['/limpeza-pos-obra'].description}
        canonical={seoData['/limpeza-pos-obra'].canonical}
      />

      {/* Structured Data */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqPageData)}
        </script>
      </Helmet>

      {/* Header */}
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/equipamentos-disponiveis" className="hover:text-blue-400 transition-colors">Catálogo</Link>
            <Link to="/limpeza-pos-obra" className="text-green-400 font-semibold">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="hover:text-blue-400 transition-colors">Blog</Link>
            <a 
              href="https://www.instagram.com/welltecbrasil/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-blue-400 transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Início</button>
              <button onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Serviços</button>
              <button onClick={() => { navigate('/equipamentos-disponiveis'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Catálogo</button>
              <button onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400 font-semibold">Limpeza Pós-Obra</button>
              <button onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Locação</button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">Blog</button>
              <a href="https://www.instagram.com/welltecbrasil/" target="_blank" rel="noopener noreferrer" className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors flex items-center">
                <Instagram className="w-5 h-5 mr-2" />
                Instagram
              </a>
              <Button onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }} className="bg-green-500 hover:bg-green-600 w-full mt-2">
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-blue-900 via-slate-800 to-slate-900 text-white py-24 md:py-32 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(30, 41, 59, 0.85)), url('https://customer-assets-4nw71qhi.emergentagent.net/job_locacao-pisos/artifacts/sqhpxfrr_Limpeza-pos%20obra%20lavadora%20de%20piso%20galpao.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/60 to-transparent"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block bg-green-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              LIMPEZA MECANIZADA PROFISSIONAL
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Limpeza Pós-Obra em BH e Região
            </h1>
            
            <p className="text-xl md:text-2xl text-blue-100 mb-6 leading-relaxed font-semibold">
              Limpeza mecanizada para galpões, indústrias, condomínios, lojas e grandes áreas após obras e reformas
            </p>

            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Utilizamos equipamentos profissionais e técnicas adequadas para remover poeira, resíduos de obra, cimento, argamassa, rejunte e sujeira pesada, preparando o imóvel para entrega, ocupação ou inauguração.
            </p>

            <p className="text-sm text-blue-200 mb-10 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Atendimento em Belo Horizonte e Região Metropolitana
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => handleWhatsAppClick('orcamento')}
                size="lg"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg shadow-xl hover:scale-105 transition-all"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Solicitar Orçamento pelo WhatsApp
              </Button>
              
              <Button 
                onClick={() => document.getElementById('como-funciona').scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg"
              >
                Como Funciona
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* NOVA SEÇÃO: Limpeza Mecanizada com Equipamentos Profissionais */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Limpeza Pós-Obra com Equipamentos Profissionais
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
                Não fazemos apenas limpeza. Combinamos mão de obra especializada, equipamentos profissionais e conhecimento técnico para aumentar a produtividade e melhorar o resultado da limpeza de grandes áreas.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-8">
                  <div className="bg-red-100 text-red-600 w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <X className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Limpeza Convencional</h3>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>Menor produtividade em grandes áreas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>Dificuldade para remover sujeira aderida</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>Mais tempo de execução</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>
                      <span>Maior esforço físico da equipe</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-500 shadow-lg">
                <CardContent className="p-8">
                  <div className="bg-green-100 text-green-600 w-14 h-14 rounded-full flex items-center justify-center mb-4 mx-auto">
                    <CheckCircle className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Limpeza Mecanizada WellTec</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Maior produtividade</strong> em grandes áreas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Equipamento selecionado</strong> conforme tipo de piso</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Redução do tempo</strong> de execução</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Conhecimento técnico</strong> dos equipamentos</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-blue-50 border-2 border-blue-200">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">A escolha do equipamento depende de:</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Tipo de piso</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Tipo de resíduo</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Nível de sujeira</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Metragem da área</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Acesso ao local</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">Objetivo da limpeza</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Seção: Uma obra só termina quando... */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Uma obra só termina quando o espaço está pronto para ser usado
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Após uma construção ou reforma, normalmente permanecem resíduos que a limpeza convencional não consegue remover com a mesma eficiência de equipamentos profissionais.
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <Card className="bg-white shadow-xl border-2 border-gray-100">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Resíduos comuns após obras e reformas:</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    'Poeira fina acumulada',
                    'Restos de cimento e argamassa',
                    'Rejunte em excesso',
                    'Manchas e marcas de tinta',
                    'Resíduos de construção',
                    'Sujeira pesada em pisos',
                    'Pó industrial',
                    'Resíduos em garagens e áreas externas',
                    'Lama e sujeira acumulada',
                    'Marcas superficiais difíceis de remover'
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 p-6 bg-green-50 rounded-lg border-l-4 border-green-600">
                  <p className="text-gray-800 font-semibold">
                    💡 A WellTec utiliza equipamentos profissionais e técnicas adequadas para remover esses resíduos de forma eficiente, entregando o imóvel pronto para uso.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-10">
            <Button 
              onClick={handleWhatsAppClick}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Precisa entregar a obra? Fale com a WellTec
            </Button>
          </div>
        </div>
      </section>

      {/* Serviços de Limpeza */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Serviços de Limpeza Pós-Obra
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Limpeza profissional com equipamentos adequados para cada tipo de espaço e necessidade
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {servicosLimpeza.map((servico) => (
              <Card key={servico.title} className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="bg-green-100 text-green-600 w-16 h-16 rounded-lg flex items-center justify-center mb-4">
                    {servico.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{servico.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{servico.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button 
              onClick={handleWhatsAppClick}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
            >
              Solicitar Avaliação
            </Button>
          </div>
        </div>
      </section>

      {/* NOVA SEÇÃO: Limpeza de Galpões Pós-Obra */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block bg-green-600 px-4 py-2 rounded-full text-sm font-bold mb-6">
                ESPECIALIDADE EM GRANDES ÁREAS
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Limpeza Pós-Obra de Galpões e Grandes Áreas
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Em galpões e grandes áreas, a limpeza manual pode demandar muito tempo e mão de obra. A utilização de equipamentos mecanizados permite aumentar a produtividade e trabalhar de forma mais eficiente em grandes superfícies.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-8">
                  <Warehouse className="w-12 h-12 text-green-500 mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-4">Galpões Logísticos</h3>
                  <p className="text-gray-300 mb-6">
                    Limpeza de grandes áreas de concreto após obras, reformas ou adequações, preparando o piso para início da operação logística.
                  </p>
                  <ul className="space-y-2 text-gray-400">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Remoção de poeira industrial</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Limpeza de piso de concreto</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Preparação para início de operação</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-8">
                  <Factory className="w-12 h-12 text-green-500 mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-4">Galpões Industriais</h3>
                  <p className="text-gray-300 mb-6">
                    Limpeza de áreas industriais com presença de resíduos de obra, cimento, argamassa e sujeira pesada em pisos industriais.
                  </p>
                  <ul className="space-y-2 text-gray-400">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Remoção de cimento e argamassa</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Limpeza mecanizada eficiente</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Equipamentos para grandes metragens</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-green-600 border-green-500">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-4">
                  Seu galpão precisa de limpeza pós-obra?
                </h3>
                <p className="text-green-50 mb-6 text-lg">
                  Informe a metragem aproximada e a cidade para recebermos uma avaliação personalizada
                </p>
                <Button 
                  onClick={() => handleWhatsAppClick('galpao')}
                  size="lg"
                  className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg font-bold"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Orçamento para Galpão
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Equipamentos Profissionais - Otimizado */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Equipamentos Profissionais Selecionados para Cada Situação
            </h2>
            <p className="text-lg text-gray-300">
              Utilizamos equipamentos profissionais selecionados conforme o tipo de piso, nível de sujeira e tamanho da área para entregar produtividade e resultado.
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <Card className="bg-slate-800 border-slate-700 hover:border-green-500 transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-green-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Truck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Lavadoras de Piso</h3>
                <p className="text-gray-400">
                  Para lavagem mecanizada e remoção de sujeira aderida em grandes áreas
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700 hover:border-green-500 transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-green-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Warehouse className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Varredeiras Industriais</h3>
                <p className="text-gray-400">
                  Para remoção de poeira e resíduos sólidos antes da lavagem
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700 hover:border-green-500 transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-green-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Wrench className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Aspiradores Profissionais</h3>
                <p className="text-gray-400">
                  Para remoção de pó e resíduos secos em cantos e áreas específicas
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700 hover:border-green-500 transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-green-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Equipamentos de Alta Pressão</h3>
                <p className="text-gray-400">
                  Para aplicações compatíveis com o tipo de superfície e ambiente
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700 hover:border-green-500 transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-green-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Equipamentos Especializados</h3>
                <p className="text-gray-400">
                  Selecionados conforme o tipo de piso, sujeira e necessidade da obra
                </p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700 hover:border-green-500 transition-all duration-300">
              <CardContent className="p-6">
                <div className="bg-green-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Produtos Adequados</h3>
                <p className="text-gray-400">
                  Produtos selecionados respeitando as características de cada piso
                </p>
              </CardContent>
            </Card>
          </div>

          {/* NOVA SEÇÃO DESTACADA: Execução ou Locação */}
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Você Precisa da Limpeza ou Apenas da Máquina?
              </h2>
              <p className="text-xl text-gray-300">
                Escolha a opção que melhor atende sua necessidade
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-gradient-to-br from-green-600 to-green-700 border-green-500">
                <CardContent className="p-8">
                  <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">OPÇÃO 1: Contrate a Execução</h3>
                  <p className="text-green-50 mb-6 text-lg">
                    Nossa equipe realiza a limpeza pós-obra utilizando equipamentos profissionais e métodos adequados para cada situação.
                  </p>
                  <ul className="space-y-3 mb-8 text-green-50">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>Equipe especializada</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>Equipamentos inclusos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>Produtos adequados</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span>Resultado garantido</span>
                    </li>
                  </ul>
                  <Button 
                    onClick={() => handleWhatsAppClick('orcamento')}
                    className="w-full bg-white text-green-600 hover:bg-gray-100 py-6 text-lg font-bold"
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Solicitar Orçamento
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-2 border-slate-600 hover:border-blue-500 transition-all">
                <CardContent className="p-8">
                  <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mb-6">
                    <Truck className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">OPÇÃO 2: Alugue o Equipamento</h3>
                  <p className="text-gray-300 mb-6 text-lg">
                    Já possui equipe para executar o serviço? A WellTec também oferece <Link to="/locacao-lavadora-piso" className="text-blue-400 hover:text-blue-300 underline font-semibold">
                      locação de lavadoras e varredeiras profissionais
                    </Link>.
                  </p>
                  <ul className="space-y-3 mb-8 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Equipamentos novos</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Suporte técnico</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Orientação de uso</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                      <span>Diária ou semanal</span>
                    </li>
                  </ul>
                  <Button 
                    onClick={() => navigate('/locacao-lavadora-piso')}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-bold"
                  >
                    Ver Opções de Locação
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Galeria - Trabalhos Realizados */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trabalhos Realizados
            </h2>
            <p className="text-lg text-gray-600">
              Limpeza profissional em galpões industriais com equipamentos especializados
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
            {/* Foto 1 - Lavadora Kärcher */}
            <Card className="border-2 border-gray-100 hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-0">
                <img 
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_locacao-pisos/artifacts/sqhpxfrr_Limpeza-pos%20obra%20lavadora%20de%20piso%20galpao.png"
                  alt="Limpeza pós-obra de galpão industrial com equipamento Kärcher profissional"
                  className="w-full h-80 object-contain bg-gray-50 rounded-t-lg"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Limpeza de Galpão Industrial
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Limpeza profissional pós-obra utilizando equipamentos Kärcher de alta performance. Remoção eficiente de resíduos, poeira e sujeira pesada.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                      ✓ Lavadora Profissional
                    </span>
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-semibold">
                      ✓ Grande Área
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Foto 2 - Varredeira */}
            <Card className="border-2 border-gray-100 hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-0">
                <img 
                  src="https://customer-assets-4nw71qhi.emergentagent.net/job_locacao-pisos/artifacts/d4lde4la_limpeza%20varredeira.jfif"
                  alt="Varredeira industrial em galpão pós-obra removendo poeira e resíduos"
                  className="w-full h-80 object-contain bg-gray-50 rounded-t-lg"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Varredura Industrial em Armazém
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Remoção de poeira e resíduos em armazém com varredeira industrial. Ideal para preparar grandes áreas após obras.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                      ✓ Varredeira Industrial
                    </span>
                    <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold">
                      ✓ Armazém
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-10">
            <Button 
              onClick={handleWhatsAppClick}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Solicitar Orçamento para Seu Projeto
            </Button>
          </div>
        </div>
      </section>

      {/* Para Quais Empresas e Obras? - Otimizado */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Para Quais Obras e Empresas?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Atendemos empresas e projetos que precisam de limpeza pós-obra mecanizada para entregar o imóvel pronto para uso
            </p>
          </div>

          <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientesAtendidos.map((cliente) => (
              <Card key={cliente.name} className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="bg-green-100 text-green-600 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                    {cliente.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{cliente.name}</h3>
                  <p className="text-gray-600 leading-relaxed">{cliente.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => handleWhatsAppClick('info')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Falar com a WellTec sobre Meu Projeto
            </Button>
          </div>
        </div>
      </section>

      {/* Como Funciona */}
      <section id="como-funciona" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Como funciona
            </h2>
            <p className="text-lg text-gray-600">
              Processo estruturado para garantir o melhor resultado
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {etapasProcesso.map((etapa) => (
              <Card key={etapa.number} className="border-2 border-gray-100 hover:border-green-500 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start gap-6">
                    <div className="bg-green-600 text-white rounded-full w-16 h-16 flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {etapa.number}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{etapa.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{etapa.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-10">
            <Button 
              onClick={handleWhatsAppClick}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Solicitar Orçamento de Limpeza Pós-Obra
            </Button>
          </div>
        </div>
      </section>

      {/* NOVA SEÇÃO: Limpeza de Piso Pós-Obra */}
      <section className="py-20 bg-white border-t-2 border-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Limpeza de Piso Pós-Obra
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                O procedimento depende do tipo de piso e dos resíduos presentes. A avaliação define o equipamento, escova, produto, método e quantidade de etapas necessárias.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-gray-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-gray-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Piso de Concreto</h3>
                  <p className="text-gray-600 text-sm">
                    Comum em galpões e áreas industriais. Requer equipamentos adequados para remoção de resíduos.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-gray-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-gray-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Pisos Industriais</h3>
                  <p className="text-gray-600 text-sm">
                    Epóxi, poliuretano ou polimento requerem produtos e técnicas específicas.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-gray-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-gray-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Cerâmica</h3>
                  <p className="text-gray-600 text-sm">
                    Comum em áreas comerciais e residenciais. Permite diversos métodos de limpeza.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-gray-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-gray-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Porcelanato</h3>
                  <p className="text-gray-600 text-sm">
                    Requer cuidados especiais na escolha de produtos e escovas.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-gray-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-gray-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Pisos Comerciais</h3>
                  <p className="text-gray-600 text-sm">
                    Vinílicos, laminados e outros revestimentos específicos.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 transition-all">
                <CardContent className="p-6">
                  <div className="bg-gray-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle className="w-6 h-6 text-gray-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Pedras Naturais</h3>
                  <p className="text-gray-600 text-sm">
                    Mármore, granito e outras pedras com tratamento específico.
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card className="bg-blue-50 border-2 border-blue-200">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">A avaliação técnica define:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <Wrench className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900">Equipamento adequado</p>
                      <p className="text-sm text-gray-600">Conforme tipo de piso e área</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900">Escova ou pad</p>
                      <p className="text-sm text-gray-600">Tipo e abrasividade</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900">Produto químico</p>
                      <p className="text-sm text-gray-600">Compatível com o piso</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-gray-900">Método e etapas</p>
                      <p className="text-sm text-gray-600">Quantidade de passadas necessárias</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Seção de Serviços Relacionados - Linkagem Interna */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 md:p-12 border-2 border-blue-100">
              <div className="flex items-start gap-4 mb-6">
                <Factory className="w-10 h-10 text-blue-600 flex-shrink-0" />
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                    Limpeza pós-obra para galpões e ambientes industriais
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    A WellTec oferece soluções de limpeza pós-obra para galpões, áreas industriais e grandes espaços comerciais. Utilizamos equipamentos profissionais de limpeza mecanizada para ajudar a remover resíduos e preparar os ambientes para uso. Consulte nossa equipe sobre a execução da limpeza e as opções de locação de lavadoras e varredeiras de piso.
                  </p>
                  <Link 
                    to="/limpeza-pos-obra-industrial"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-md hover:shadow-lg"
                  >
                    Conheça a limpeza pós-obra industrial
                    <ChevronRight className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Links Relacionados */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Serviços relacionados:</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Link 
                  to="/limpeza-pos-obra-galpoes"
                  className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <Warehouse className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-600">Limpeza de Galpões</p>
                    <p className="text-sm text-gray-600">Pós-obra em grandes áreas</p>
                  </div>
                </Link>
                
                <Link 
                  to="/limpeza-piso-pos-obra"
                  className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <Sparkles className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-600">Limpeza de Piso Pós-Obra</p>
                    <p className="text-sm text-gray-600">Especializado em pisos</p>
                  </div>
                </Link>

                <Link 
                  to="/locacao-lavadora-piso"
                  className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <Truck className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-600">Locação de Equipamentos</p>
                    <p className="text-sm text-gray-600">Alugue lavadoras e varredeiras</p>
                  </div>
                </Link>

                <Link 
                  to="/limpeza-pos-obra-betim"
                  className="flex items-center gap-3 p-4 bg-white border-2 border-gray-100 rounded-lg hover:border-blue-500 hover:shadow-md transition-all group"
                >
                  <MapPin className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="font-semibold text-gray-900 group-hover:text-blue-600">Limpeza em Betim</p>
                    <p className="text-sm text-gray-600">Atendimento regional</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Perguntas Frequentes
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-white border-2 border-gray-100 rounded-lg px-6">
                  <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-green-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-10 text-center p-6 bg-green-50 rounded-lg border-2 border-green-200">
              <p className="text-gray-800 font-semibold mb-4">
                Tem outras dúvidas? Entre em contato!
              </p>
              <Button 
                onClick={handleWhatsAppClick}
                className="bg-green-600 hover:bg-green-700"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Falar no WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Regiões Atendidas */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Atendemos Belo Horizonte e Região Metropolitana
            </h2>
            <p className="text-lg text-blue-100 mb-8">
              Limpeza pós-obra profissional em BH, Contagem, Betim, Nova Lima, Mário Campos e região
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-10">
              {['Belo Horizonte', 'Contagem', 'Betim', 'Nova Lima', 'Mário Campos', 'Região Metropolitana'].map((cidade) => (
                <div key={cidade} className="bg-blue-800/50 backdrop-blur-sm rounded-lg p-4 border border-blue-700">
                  <p className="font-semibold">{cidade}</p>
                </div>
              ))}
            </div>

            <Button 
              onClick={() => handleWhatsAppClick('info')}
              size="lg"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg font-bold"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Enviar Informações da Obra
            </Button>
          </div>
        </div>
      </section>

      {/* NOVA SEÇÃO: Páginas Estratégicas B2B */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Limpeza Pós-Obra por Segmento
              </h2>
              <p className="text-lg text-gray-600">
                Serviços especializados para diferentes tipos de projetos
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer group" onClick={() => navigate('/limpeza-pos-obra-galpoes')}>
                <CardContent className="p-8 text-center">
                  <div className="bg-green-100 text-green-600 w-20 h-20 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <Warehouse className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Limpeza de Galpões
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Especialistas em galpões logísticos, industriais e grandes áreas. Equipamentos de alta produtividade.
                  </p>
                  <span className="text-green-600 font-semibold flex items-center justify-center gap-2 group-hover:gap-3 transition-all">
                    Saiba mais <ChevronRight className="w-5 h-5" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-blue-500 hover:shadow-xl transition-all cursor-pointer group" onClick={() => navigate('/limpeza-pos-obra-industrial')}>
                <CardContent className="p-8 text-center">
                  <div className="bg-blue-100 text-blue-600 w-20 h-20 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <Factory className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Limpeza Industrial
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Experiência com pisos industriais (epóxi, poliuretano) e preparação de ambientes para validação.
                  </p>
                  <span className="text-blue-600 font-semibold flex items-center justify-center gap-2 group-hover:gap-3 transition-all">
                    Saiba mais <ChevronRight className="w-5 h-5" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-purple-500 hover:shadow-xl transition-all cursor-pointer group" onClick={() => navigate('/limpeza-piso-pos-obra')}>
                <CardContent className="p-8 text-center">
                  <div className="bg-purple-100 text-purple-600 w-20 h-20 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                    <Sparkles className="w-10 h-10" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Limpeza de Piso
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Guia técnico completo por tipo de piso: concreto, cerâmica, porcelanato, mármore e mais.
                  </p>
                  <span className="text-purple-600 font-semibold flex items-center justify-center gap-2 group-hover:gap-3 transition-all">
                    Saiba mais <ChevronRight className="w-5 h-5" />
                  </span>
                </CardContent>
              </Card>
            </div>

            <div className="mt-12 text-center">
              <Card className="bg-gradient-to-r from-green-600 to-green-700 border-green-500 max-w-3xl mx-auto">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Precisa de Equipamentos para Fazer a Limpeza?
                  </h3>
                  <p className="text-green-100 mb-6 text-lg">
                    Também oferecemos <strong>locação de lavadoras e varredeiras profissionais</strong> para empresas que possuem equipe própria
                  </p>
                  <Button 
                    onClick={() => navigate('/locacao-lavadora-piso')}
                    size="lg"
                    className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg font-bold"
                  >
                    Ver Opções de Locação
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* NOVA SEÇÃO: Conteúdos sobre Limpeza Pós-Obra (Blog) */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Conteúdos sobre Limpeza Pós-Obra
              </h2>
              <p className="text-lg text-gray-600">
                Artigos e guias para ajudar você a entender melhor o processo
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/blog/custo-limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Award className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Quanto Custa Limpeza Pós-Obra?
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Guia completo com fatores que influenciam o preço e tabelas de valores
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ler artigo <ChevronRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/blog/limpeza-galpao-industrial')}>
                <CardContent className="p-6">
                  <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Warehouse className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Limpeza de Galpão Industrial
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Equipamentos e técnicas para limpeza de grandes áreas industriais
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ler artigo <ChevronRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>

              <Card className="border-2 border-gray-100 hover:border-green-500 hover:shadow-xl transition-all cursor-pointer" onClick={() => navigate('/blog/equipamentos-limpeza-pos-obra')}>
                <CardContent className="p-6">
                  <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Wrench className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">
                    Equipamentos Profissionais
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Conheça os principais equipamentos utilizados em limpeza pós-obra
                  </p>
                  <span className="text-green-600 font-semibold text-sm flex items-center gap-2">
                    Ler artigo <ChevronRight className="w-4 h-4" />
                  </span>
                </CardContent>
              </Card>
            </div>

            <div className="text-center mt-10">
              <Button 
                onClick={() => navigate('/blog')}
                variant="outline"
                size="lg"
                className="border-2 border-gray-300 hover:border-green-600 hover:bg-green-50"
              >
                Ver Todos os Artigos
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Formulário de Contato */}
      <ContactForm />

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
                alt="WellTec" 
                className="h-16 mb-4"
              />
              <p className="text-gray-400 text-sm">
                Equipamentos profissionais e serviços de limpeza pós-obra em BH e região.
              </p>
            </div>

            <div>
              <h3 className="font-bold mb-4">Serviços</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link></li>
                <li><Link to="/locacao-lavadora-piso" className="hover:text-green-400 transition-colors">Locação de Equipamentos</Link></li>
                <li><Link to="/servicos" className="hover:text-green-400 transition-colors">Manutenção</Link></li>
                <li><Link to="/reforma-tennant-m20" className="hover:text-green-400 transition-colors">Reforma de Equipamentos</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Empresa</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-green-400 transition-colors">Sobre a WellTec</Link></li>
                <li><Link to="/blog" className="hover:text-green-400 transition-colors">Blog</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold mb-4">Contato</h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href="tel:+5531971823249" className="hover:text-green-400 transition-colors">(31) 97182-3249</a>
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  <button onClick={handleWhatsAppClick} className="hover:text-green-400 transition-colors">WhatsApp</button>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1" />
                  <span className="text-sm">Mário Campos - MG<br />Região Metropolitana de BH</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec - Locações e Serviços Técnicos. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      {/* Botão Flutuante WhatsApp */}
      <button
        onClick={handleWhatsAppClick}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50"
        aria-label="WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Schema.org Structured Data */}
      
    </div>
  );
};

export default LimpezaPosObra;
